import { jwtDecode } from 'jwt-decode';
import { getConfig } from '../config';
import type { AuthTokens, DecodedToken } from '../types/config';

const STORAGE_KEY = 'dt_platform_auth';
const PKCE_KEY = 'dt_platform_pkce';
const PRE_AUTH_URI_KEY = 'dt_platform_pre_auth_uri';
const CLIENT_ID_KEY = 'dt_platform_client_id';

interface PKCECodes {
  codeVerifier: string;
  codeChallenge: string;
  createdAt: Date;
}

// Event for auth state changes
type AuthEventType = 'tokenRefreshed' | 'authFailed' | 'logout';
type AuthEventListener = (event: AuthEventType, data?: unknown) => void;

const authEventListeners: AuthEventListener[] = [];

export function onAuthEvent(listener: AuthEventListener): () => void {
  authEventListeners.push(listener);
  return () => {
    const index = authEventListeners.indexOf(listener);
    if (index > -1) authEventListeners.splice(index, 1);
  };
}

function emitAuthEvent(event: AuthEventType, data?: unknown): void {
  authEventListeners.forEach(listener => listener(event, data));
}

// Generate random string for PKCE
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// Create SHA256 hash
async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return await crypto.subtle.digest('SHA-256', data);
}

// Base64 URL encode
function base64UrlEncode(arrayBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Create PKCE codes
async function createPKCECodes(): Promise<PKCECodes> {
  const codeVerifier = generateRandomString(64);
  const hashed = await sha256(codeVerifier);
  const codeChallenge = base64UrlEncode(hashed);

  return {
    codeVerifier,
    codeChallenge,
    createdAt: new Date(),
  };
}

// URL encode object for form data
function toUrlEncoded(obj: Record<string, string>): string {
  return Object.entries(obj)
    .map(([key, value]) => {
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      return `${encodeURIComponent(snakeKey)}=${encodeURIComponent(value)}`;
    })
    .join('&');
}

export class AuthService {
  private clientId: string;
  private redirectUri: string;
  private scopes: string[];
  private passportServiceBaseUrl: string;
  private refreshTimeout?: ReturnType<typeof setTimeout>;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<AuthTokens | null> | null = null;
  // Dedup guards
  private initializePromise: Promise<boolean> | null = null;
  private isExchangingCode: boolean = false;
  private exchangePromise: Promise<AuthTokens> | null = null;

  constructor(clientId: string) {
    const config = getConfig();
    this.clientId = clientId;
    this.redirectUri = config.baseRoot;
    this.scopes = ['read', 'write'];
    this.passportServiceBaseUrl = config.passportServiceOrigin + '/passportsvc/api';
  }

  get authorizeEndpoint(): string {
    return `${this.passportServiceBaseUrl}/v2/oauth/authorize`;
  }

  get tokenEndpoint(): string {
    return `${this.passportServiceBaseUrl}/v2/oauth/token`;
  }

  // Initialize - check for auth code in URL
  async initialize(): Promise<boolean> {
    // Deduplicate initialize calls (e.g., React StrictMode double effect)
    if (this.initializePromise) {
      return this.initializePromise;
    }

    this.initializePromise = (async () => {
      const code = this.getCodeFromLocation();
      const alreadyAuthenticated = this.isAuthenticated();
      // eslint-disable-next-line no-console
      console.debug('[Auth.initialize] start', {
        hasCode: !!code,
        alreadyAuthenticated,
      });

      if (code) {
        // If we've already consumed this code (e.g., due to double init), skip re-exchange
        let codeConsumed = false;
        try {
          const consumed = sessionStorage.getItem('oauth_code_consumed');
          codeConsumed = consumed === code;
        } catch {}

        if (codeConsumed) {
          // eslint-disable-next-line no-console
          console.debug('[Auth.initialize] OAuth code already consumed – stripping from URL and continuing');
          this.removeCodeFromLocation();
          return this.isAuthenticated();
        }

        // If we are already authenticated, just strip the code and continue
        if (alreadyAuthenticated) {
          // eslint-disable-next-line no-console
          console.debug('[Auth.initialize] Already authenticated, skipping code exchange and stripping code from URL');
          this.removeCodeFromLocation();
          this.startRefreshTimer();
          return true;
        }

        console.log('Found OAuth code, attempting to exchange...');
        // Mark exchange in progress so other parts of app can avoid clearing PKCE
        try { sessionStorage.setItem('oauth_exchange_in_progress', '1'); } catch {}
        try {
          await this.exchangeCodeForTokens(code);
          // Mark this code as consumed to avoid any re-exchange on re-inits
          try { sessionStorage.setItem('oauth_code_consumed', code); } catch {}
          console.log('Code exchange successful!');
          // IMPORTANT: Remove code from current URL BEFORE navigating to the restore URI
          this.removeCodeFromLocation();
          this.restoreUri();
          return true;
        } catch (error) {
          console.error('Failed to exchange code for tokens:', error);
          this.clearStorage();
          // Remove code from URL and redirect to clean login page
          const baseUrl = window.location.origin + window.location.pathname;
          window.location.href = baseUrl + '#/login';
          return false;
        } finally {
          try { sessionStorage.removeItem('oauth_exchange_in_progress'); } catch {}
        }
      }

      // Check if we have valid tokens
      if (this.isAuthenticated()) {
        console.log('Found valid tokens');
        this.startRefreshTimer();
        return true;
      }

      console.log('No valid auth found');
      return false;
    })();

    try {
      return await this.initializePromise;
    } finally {
      this.initializePromise = null;
    }
  }

  // Start OAuth PKCE flow
  async authorize(inviteId?: string): Promise<void> {
    const pkce = await createPKCECodes();
    localStorage.setItem(PKCE_KEY, JSON.stringify(pkce));
    localStorage.setItem(PRE_AUTH_URI_KEY, window.location.href);
    // Save the client ID so we can use it after OAuth redirect
    localStorage.setItem(CLIENT_ID_KEY, this.clientId);

    const params: Record<string, string> = {
      clientId: this.clientId,
      responseType: 'code',
      redirectUri: this.redirectUri,
      scope: this.scopes.join(' '),
      codeChallenge: pkce.codeChallenge,
      codeChallengeMethod: 'S256',
    };

    if (inviteId) {
      params.inviteId = inviteId;
    }

    const queryString = toUrlEncoded(params);
    // eslint-disable-next-line no-console
    console.debug('[Auth.authorize] Redirecting to authorize endpoint with clientId:', this.clientId, 'redirectUri:', this.redirectUri);
    // Helpful if a popup/redirect blocker interferes
    // eslint-disable-next-line no-console
    console.debug('[Auth.authorize] URL =', `${this.authorizeEndpoint}?${queryString}`);
    window.location.href = `${this.authorizeEndpoint}?${queryString}`;
  }

  // Exchange authorization code for tokens
  private async exchangeCodeForTokens(code: string): Promise<AuthTokens> {
    // Deduplicate concurrent exchanges
    if (this.exchangePromise) {
      return this.exchangePromise;
    }
    if (this.isExchangingCode) {
      // Should not happen if exchangePromise is set, but guard anyway
      return new Promise<AuthTokens>((resolve, reject) => {
        const check = () => {
          if (!this.isExchangingCode) {
            if (this.getAccessToken()) {
              const tokens = this.getTokens();
              if (tokens) return resolve(tokens);
            }
            return reject(new Error('Exchange finished without tokens'));
          }
          setTimeout(check, 50);
        };
        check();
      });
    }

    this.isExchangingCode = true;
    this.exchangePromise = (async () => {
      const pkceData = localStorage.getItem(PKCE_KEY);
      if (!pkceData) {
        throw new Error('PKCE data not found');
      }

      const pkce: PKCECodes = JSON.parse(pkceData);

    const payload = {
      grantType: 'authorization_code',
      clientId: this.clientId,
      redirectUri: this.redirectUri,
      code: code.replace('#/', ''),
      codeVerifier: pkce.codeVerifier,
    };

    const response = await fetch(this.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: toUrlEncoded(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || 'Failed to exchange code');
    }

    const tokens: AuthTokens = await response.json();
    this.setTokens(tokens);
    localStorage.removeItem(PKCE_KEY);

    return tokens;
    })();

    try {
      return await this.exchangePromise;
    } finally {
      this.isExchangingCode = false;
      this.exchangePromise = null;
    }
  }

  // Refresh access token - with deduplication
  async refreshTokens(): Promise<AuthTokens | null> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    const tokens = this.getTokens();
    if (!tokens?.refresh_token) {
      console.warn('No refresh token available');
      return null;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefreshTokens(tokens.refresh_token);

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefreshTokens(refreshToken: string): Promise<AuthTokens | null> {
    try {
      console.log('Refreshing access token...');

      const payload = {
        grantType: 'refresh_token',
        clientId: this.clientId,
        refreshToken: refreshToken,
      };

      const response = await fetch(this.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: toUrlEncoded(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token refresh failed:', response.status, errorText);
        throw new Error('Failed to refresh token');
      }

      const newTokens: AuthTokens = await response.json();

      // Keep refresh token if not returned
      if (!newTokens.refresh_token) {
        newTokens.refresh_token = refreshToken;
      }

      this.setTokens(newTokens);
      console.log('Token refreshed successfully');
      emitAuthEvent('tokenRefreshed', newTokens);
      return newTokens;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Don't clear storage yet - let the caller decide
      return null;
    }
  }

  // Handle 401 error - try to refresh token, if that fails, trigger re-auth
  async handleUnauthorized(): Promise<boolean> {
    console.log('Handling 401 Unauthorized - attempting token refresh');

    const newTokens = await this.refreshTokens();

    if (newTokens) {
      return true; // Successfully refreshed
    }

    // Refresh failed - need to re-authenticate
    console.log('Token refresh failed - re-authentication required');
    emitAuthEvent('authFailed');
    this.clearStorage();

    // Redirect to login
    window.location.href = this.redirectUri + '#/login';
    return false;
  }

  // Force re-authentication (clears tokens and redirects)
  forceReauth(): void {
    this.clearStorage();
    emitAuthEvent('authFailed');
    window.location.href = this.redirectUri + '#/login';
  }

  // Logout
  // If endSession is true, redirect to provider's logout endpoint for full OAuth logout
  logout(endSession?: boolean): void {
    this.stopRefreshTimer();
    this.clearStorage();
    // NOTE: Do NOT clear CLIENT_ID_KEY here. We want to preserve the last selected
    // application id across logout so that the post-redirect flow knows which app
    // we are working with. If you need to force clearing, call clearAllAuthData().
    try { localStorage.removeItem('dt_platform_app_state'); } catch {}
    emitAuthEvent('logout');
    
    if (endSession) {
      // Redirect to OAuth provider's end session endpoint for full logout
      // Note: post_logout_redirect_uri should NOT include hash fragments (#)
      // The hash fragment is client-side only and won't be sent to the server
      // After redirect, the app will detect it's at root and show login page
      const logoutUrl = `${this.passportServiceBaseUrl}/endsession?post_logout_redirect_uri=${encodeURIComponent(this.redirectUri)}&client_id=${encodeURIComponent(this.clientId)}`;
      window.location.href = logoutUrl;
    } else {
      window.location.href = this.redirectUri + '#/login';
    }
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    const tokens = this.getTokens();
    if (!tokens?.access_token) {
      return false;
    }

    // Check if token is expired
    if (tokens.expires_at && Date.now() >= tokens.expires_at) {
      return false;
    }

    return true;
  }

  // Get access token
  getAccessToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.access_token || null;
  }

  // Get decoded user info from token
  getUser(): DecodedToken | null {
    const tokens = this.getTokens();
    if (!tokens?.access_token) {
      return null;
    }

    try {
      return jwtDecode<DecodedToken>(tokens.access_token);
    } catch {
      return null;
    }
  }

  // Private methods
  private getTokens(): AuthTokens | null {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  }

  private setTokens(tokens: AuthTokens): void {
    // Calculate expiry time
    const expiresAt = Date.now() + (tokens.expires_in - 30) * 1000; // 30 second buffer
    tokens.expires_at = expiresAt;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    this.startRefreshTimer();
  }

  private clearStorage(): void {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PKCE_KEY);
    localStorage.removeItem(PRE_AUTH_URI_KEY);
    // NOTE: Intentionally preserve CLIENT_ID_KEY during routine clears so that
    // the selected application persists across logout or token resets. Use
    // clearAllAuthData() when you explicitly want to remove CLIENT_ID_KEY.
    console.log('Auth storage cleared (client id preserved)');
  }

  private getCodeFromLocation(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('code');
  }

  private removeCodeFromLocation(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    window.history.replaceState({}, '', url.toString());
    // eslint-disable-next-line no-console
    console.debug('[Auth] Removed code from URL');
  }

  private restoreUri(): void {
    const uri = localStorage.getItem(PRE_AUTH_URI_KEY);
    localStorage.removeItem(PRE_AUTH_URI_KEY);
    // Keep CLIENT_ID_KEY - we need it to know which app we're authenticated with!

    // Don't restore to login page - go to home instead
    if (uri && !uri.includes('/login') && !uri.includes('#/login')) {
      try {
        const u = new URL(uri, window.location.origin);
        // Ensure no lingering code param
        u.searchParams.delete('code');
        window.location.href = u.toString();
      } catch {
        window.location.href = uri;
      }
    } else {
      // Clear the code from URL and go to home
      const baseUrl = window.location.origin + window.location.pathname;
      window.location.href = baseUrl + '#/';
    }
  }

  private startRefreshTimer(): void {
    this.stopRefreshTimer();

    const tokens = this.getTokens();
    if (!tokens?.expires_at || !tokens.refresh_token) {
      return;
    }

    const timeout = tokens.expires_at - Date.now() - 60000; // Refresh 1 minute before expiry

    if (timeout > 0) {
      this.refreshTimeout = setTimeout(() => {
        this.refreshTokens();
      }, timeout);
    } else {
      // Token is about to expire or already expired, refresh now
      this.refreshTokens();
    }
  }

  private stopRefreshTimer(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = undefined;
    }
  }
}

// Factory function to create auth service
export function createAuthService(clientId: string): AuthService {
  return new AuthService(clientId);
}

// Clear all stored auth data (useful when stuck with bad token)
export function clearAllAuthData(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(PKCE_KEY);
  localStorage.removeItem(PRE_AUTH_URI_KEY);
  localStorage.removeItem(CLIENT_ID_KEY);
  localStorage.removeItem('dt_platform_app_state'); // Also clear app state
  console.log('All auth data cleared');
}

// Get the pending client ID (used after OAuth redirect)
export function getPendingClientId(): string | null {
  return localStorage.getItem(CLIENT_ID_KEY);
}

// Check if there's a pending OAuth code in the URL
export function hasPendingOAuthCode(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('code');
}
